<template>
  <el-dialog
    :title="$t('emg.listMsg.editEmailTemplateTitle')"
    :visible="visible"
    width="700px"
    @close="$emit('close')"
    :close-on-click-modal="false"
  >
    <!-- 載入狀態 -->
    <div v-if="loading" class="state-container">
      <i class="el-icon-loading"></i> {{ $t('emg.listMsg.loadingTemplate') }}
    </div>
    
    <!-- 錯誤狀態 -->
    <div v-if="errorMessage" class="state-container error-state">
      <p>{{ $t('emg.listMsg.loadTemplateFailed') }}</p>
      <pre>{{ errorMessage }}</pre>
      <el-button @click="fetchTemplates" size="mini">{{ $t('emg.bt.retry') }}</el-button>
    </div>

    <!-- 主表單 -->
    <el-form v-if="!loading && !errorMessage" label-position="top">
      <p class="template-info">
        {{ $t('emg.listMsg.templateInfoPart1') }} <code>{pno}</code> {{ $t('emg.listMsg.templateInfoPart2') }}
      </p>

      <div class="template-section">
        <h3>{{ $t('emg.listMsg.templateSectionGM') }}</h3>
        <el-form-item :label="$t('emg.listMsg.recipientTemplate', { type: 'GM' })">
          <el-input v-model="templates.templateToGM" :placeholder="$t('emg.listMsg.recipientPlaceholder', { example: 'gm-{pno}@example.com' })"></el-input>
        </el-form-item>
        <el-form-item :label="$t('emg.listMsg.subjectTemplate', { type: 'GM' })">
          <el-input v-model="templates.templateSubjectGM" :placeholder="$t('emg.listMsg.subjectPlaceholderGM', { pno: '{pno}' })"></el-input>
        </el-form-item>
        <el-form-item :label="$t('emg.listMsg.bodyTemplate', { type: 'GM' })">
          <el-input type="textarea" :rows="5" v-model="templates.templateBodyGM" :placeholder="$t('emg.listMsg.bodyPlaceholderGM')"></el-input>
        </el-form-item>
      </div>

      <div class="template-section">
        <h3>{{ $t('emg.listMsg.templateSectionCX') }}</h3>
        <el-form-item :label="$t('emg.listMsg.recipientTemplate', { type: 'CX' })">
          <el-input v-model="templates.templateToCX" :placeholder="$t('emg.listMsg.recipientPlaceholder', { example: 'cx-support-{pno}@example.com' })"></el-input>
        </el-form-item>
        <el-form-item :label="$t('emg.listMsg.subjectTemplate', { type: 'CX' })">
          <el-input v-model="templates.templateSubjectCX" :placeholder="$t('emg.listMsg.subjectPlaceholderCX', { pno: '{pno}' })"></el-input>
        </el-form-item>
        <el-form-item :label="$t('emg.listMsg.bodyTemplate', { type: 'CX' })">
          <el-input type="textarea" :rows="5" v-model="templates.templateBodyCX" :placeholder="$t('emg.listMsg.bodyPlaceholderCX')"></el-input>
        </el-form-item>
      </div>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="$emit('close')">{{ $t('emg.bt.cancel') }}</el-button>
      <el-button 
        type="primary" 
        @click="saveTemplates"
        :loading="isSaving"
        :disabled="loading || !!errorMessage">
        {{ isSaving ? $t('emg.bt.saving') : $t('emg.bt.saveSettings') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
  import URL from '@/views/emg/util/url'

  export default {
    name: 'TemplateModal',

    props: {
      visible: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        loading: false,
        isSaving: false,
        errorMessage: null,
        templates: {
          templateToGM: '',
          templateSubjectGM: '',
          templateBodyGM: '',
          templateToCX: '',
          templateSubjectCX: '',
          templateBodyCX: ''
        }
      };
    },

    watch: {    // 當彈窗變為可見時，自動載入資料
      visible(newVal) {
        if (newVal) {
          this.fetchTemplates();
        }
      }
    },
    
    methods: {
      async fetchTemplates() {
        this.loading = true;
        this.errorMessage = null;
        try {
          const response = await this.$axios.get(URL.dden.getTemplates);
          if (response.result) {
            this.templates = response.result;
          }
        } catch (error) {
          this.errorMessage = this.parseApiError(error);
          console.error(this.errorMessage + ' |', error);
        } finally {
          this.loading = false;
        }
      },
      async saveTemplates() {
        this.isSaving = true;
        try {
          await this.$axios.post(URL.dden.saveTemplates, this.templates, {
              headers: {
                  'Content-Type': 'application/json'
              }
          });
          this.$emit('save-success', this.$t('emg.listMsg.saveSuccess'));
          this.$emit('close');
        } catch (error) {
          const parsedError = this.parseApiError(error);
          console.error(parsedError + ' |', error);
          this.$emit('save-error', this.$t('emg.listMsg.saveError', { error: parsedError }));
        } finally {
          this.isSaving = false;
        }
      },
      /**
      * 解析來自 Axios 的 API 錯誤，返回一個使用者友好的字串。
      * @param {Error} error - Axios 拋出的錯誤物件。
      * @returns {string} - 解析後的錯誤訊息。
      */
      parseApiError(error) {
        if (error.response && error.response.data && error.response.data.message) {
          // 後端返回了標準的錯誤結構
          return this.$t('emg.msg.apiError', { status: error.response.status, message: error.response.data.message });
        } else if (error.request) {
          // 請求已發出，但沒有收到回應 (例如網路錯誤)
          return this.$t('emg.msg.networkError');
        } else {
          // 在設定請求時發生了錯誤
          return error.message || this.$t('emg.msg.unknownError');
        }
      }
    }
  };
</script>

<style scoped>
.state-container {
  text-align: center;
  padding: 40px 20px;
  color: #5f6368;
}
.error-state pre {
    background-color: #fef0f0;
    color: #f56c6c;
    padding: 10px;
    border-radius: 4px;
    text-align: left;
    white-space: pre-wrap;
    word-break: break-all;
    margin-top: 10px;
}
.template-section {
  border: 1px solid #e0e0e0;
  padding: 15px 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}
.template-section h3 {
  font-size: 16px;
  margin-top: 0;
  color: #1a73e8;
  margin-bottom: 15px;
}
.template-info {
  font-size: 13px;
  color: #5f6368;
  margin-bottom: 20px;
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
}
</style>
